import Jed, { JedOptions } from 'jed';
// @ts-ignore
import monitor from './monitor';

class I18n {
  language: string;
  private phraseappEditorEnabled: boolean;
  private bcp47: string;
  private jed: Jed;

  constructor(
    translations: JedOptions,
    phraseappEnabled: boolean,
    language: string,
    bcp47: string,
  ) {
    this.phraseappEditorEnabled = phraseappEnabled;
    this.language = language;
    this.bcp47 = bcp47;
    this.jed = new Jed({
      ...translations,
      missing_key_callback: (key: string) => {
        /* eslint-disable-next-line no-console */
        console.warn(`"${key}" is missing from translations`);
      },
    });
  }

  gettext(key: string, ...args: any[]): string {
    if (this.phraseappEditorEnabled) return `{{__phrase_${key}__}}`;

    if (args.length > 0) {
      const translatedKey = this.jed.gettext(key);
      let translatedWithArgs;

      // There may be rare cases where the translators translate the variable placeholder:
      // this will prevent the app from crashing, and return the translated key
      try {
        translatedWithArgs = this.jed.sprintf(translatedKey, ...args);
      } catch (err) {
        monitor.error(err, {
          tags: { from: 'lib/utils/i18n.gettext' },
        });
        translatedWithArgs = translatedKey;
      }

      return translatedWithArgs;
    }

    let translation = key;
    try {
      translation = this.jed.gettext(key);
    } catch (err) {
      monitor.error(err, {
        tags: { from: 'lib/utils/i18n.gettext' },
      });
    }

    return translation;
  }

  ngettext(
    singularKey: string,
    pluralKey: string,
    value: number,
    namedArgs?: object,
  ): string {
    if (this.phraseappEditorEnabled) return `{{__phrase_${singularKey}__}}`;
    let translated;

    // There may be rare cases where the translators translate the variable placeholder:
    // this will prevent the app from crashing, and return the key
    try {
      translated = this.jed.ngettext(singularKey, pluralKey, value);
      if (namedArgs) {
        translated = this.jed.sprintf(translated, namedArgs);
      }
    } catch (err) {
      monitor.error(err, {
        tags: { from: 'lib/utils/i18n.ngettext' },
      });
      translated = singularKey;
    }

    return translated;
  }

  formatCurrency(
    currency: string,
    price: number,
    options?: Intl.NumberFormatOptions & { divisor: number },
  ): string {
    const { divisor, ...formattedOptions } = Object.assign(
      { divisor: 100 },
      options,
    );

    // NumberFormat will not support a language range wildcard
    const locale = this.bcp47 === '*' ? this.language : this.bcp47;

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      ...formattedOptions,
    }).format(price / divisor);
  }

  formatDate(
    isoDateTimeString: string | number | Date,
    options?: Intl.DateTimeFormatOptions,
  ) {
    const locale = this.bcp47;
    const date = new Date(isoDateTimeString);
    return new Intl.DateTimeFormat(locale, options).format(date);
  }
}

export default I18n;
